/**
 * GapScreamer — the persistent, impossible-to-ignore gap banner.
 *
 * Per Dipali doctrine (Apr-26): "The gap should SCREAM at every leader, every
 * time they open ScaleBoard. Not a number in a spreadsheet — a daily reality
 * check."
 *
 * Used in two places:
 *   1. <DivisionGapScreamer /> — top of every division dashboard.
 *      Shows that single division's gap, days remaining, projected close.
 *   2. <GroupGapScreamer />   — top of CEO dashboard.
 *      Shows the GROUP gap PLUS a one-line gap row per division.
 *
 * Math (Indian FY runs 1 Apr → 31 Mar):
 *   day_of_fy        = days since 1 Apr (1-365)
 *   gap              = annual_target - ytd_revenue
 *   needed_per_day   = gap / days_remaining
 *   current_pace     = ytd_revenue / day_of_fy
 *   projected_fy     = current_pace * 365
 *   projected_gap    = annual_target - projected_fy
 *
 * Color buckets (based on PROJECTED finish, not today's number):
 *   🟢 Green: projected_fy >= 100% of target
 *   🟡 Amber: projected_fy 80% - 99% of target
 *   🔴 Red:   projected_fy < 80% of target
 */
import React, { useState } from 'react';
import { TrendingUp, TrendingDown, Target, AlertCircle, CheckCircle, Sparkles, X, Trophy, Rocket, Repeat } from 'lucide-react';
import api from '@/lib/api';

// ---------- math --------------------------------------------------------

function fyContext(now = new Date()) {
  // Indian FY: 1 Apr → 31 Mar
  const y = now.getFullYear();
  const m = now.getMonth();    // 0-indexed
  const d = now.getDate();
  const fyStartYear = m >= 3 ? y : y - 1;
  const fyStart = new Date(fyStartYear, 3, 1);   // Apr 1
  const fyEnd = new Date(fyStartYear + 1, 2, 31); // Mar 31
  const totalDays = Math.round((fyEnd - fyStart) / 86400000) + 1;
  const dayOfFy = Math.max(1, Math.round((now - fyStart) / 86400000) + 1);
  const daysRemaining = Math.max(0, totalDays - dayOfFy);
  const fyLabel = `FY ${String(fyStartYear).slice(2)}-${String(fyStartYear + 1).slice(2)}`;
  return { dayOfFy, daysRemaining, totalDays, fyLabel };
}

export function computeGap({ target, realised }, now = new Date()) {
  const t = Math.max(0, Number(target) || 0);
  const r = Math.max(0, Number(realised) || 0);
  const { dayOfFy, daysRemaining, totalDays, fyLabel } = fyContext(now);
  const gap = Math.max(0, t - r);
  const neededPerDay = daysRemaining > 0 ? gap / daysRemaining : 0;
  const currentPace = dayOfFy > 0 ? r / dayOfFy : 0;
  const projectedFy = currentPace * totalDays;
  const projectedGap = t - projectedFy;
  const projectedPct = t > 0 ? (projectedFy / t) * 100 : 0;
  const realisedPct = t > 0 ? (r / t) * 100 : 0;
  let color = 'red';
  if (projectedPct >= 100) color = 'green';
  else if (projectedPct >= 80) color = 'amber';
  return {
    target: t, realised: r, gap, neededPerDay, currentPace, projectedFy,
    projectedGap, projectedPct, realisedPct, dayOfFy, daysRemaining,
    totalDays, fyLabel, color,
    aheadBy: projectedGap < 0 ? Math.abs(projectedGap) : 0,
    shortBy:  projectedGap > 0 ? projectedGap : 0,
  };
}

// ---------- formatting --------------------------------------------------

const fmtCr = (v) => {
  const n = Number(v) || 0;
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(2)}Cr`;
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(2)}L`;
  if (n >= 1e3) return `₹${(n / 1e3).toFixed(1)}K`;
  return `₹${Math.round(n)}`;
};

const palette = {
  red:   { bg: 'linear-gradient(135deg,#7a1f1f 0%,#a32d2d 100%)', accent: '#FFD7C2', stripe: '#FCEBEB', label: 'GAP ALERT', icon: AlertCircle },
  amber: { bg: 'linear-gradient(135deg,#7a4f0b 0%,#b27013 100%)', accent: '#FFE4B5', stripe: '#FAEEDA', label: 'BEHIND PACE', icon: TrendingDown },
  green: { bg: 'linear-gradient(135deg,#1f5736 0%,#2D8B5E 100%)', accent: '#CDEAD8', stripe: '#EAF3DE', label: 'ON TRACK',  icon: CheckCircle },
};

// ---------- division-level banner ---------------------------------------

export function DivisionGapScreamer({ divisionId, divisionName, target, realised, leaderName, compact = false }) {
  const [showCatchUp, setShowCatchUp] = useState(false);
  if (!target || target <= 0) return null;
  const g = computeGap({ target, realised });
  const p = palette[g.color];
  const Icon = p.icon;
  const showCatchUpBtn = g.color !== 'green' && divisionId;

  return (
    <div
      data-testid={`gap-screamer-${divisionName?.toLowerCase().replace(/\s+/g, '-')}`}
      className="rounded-xl shadow-lg overflow-hidden"
      style={{ background: p.bg }}
    >
      {/* top row: label + division */}
      <div className="px-5 pt-4 pb-2 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Icon size={20} style={{ color: p.accent }} />
          <span className="text-[11px] tracking-[0.18em] font-bold" style={{ color: p.accent }}>
            {p.label} · {divisionName?.toUpperCase()}
          </span>
        </div>
        <span className="text-[11px]" style={{ color: p.accent, opacity: 0.9 }}>
          Day {g.dayOfFy} of {g.totalDays} · {g.fyLabel}{leaderName ? ` · Owner: ${leaderName}` : ''}
        </span>
      </div>

      {/* hero numbers */}
      <div className="px-5 pb-4 grid grid-cols-2 sm:grid-cols-4 gap-4 text-white">
        <div>
          <div className="text-[10px] uppercase tracking-wider opacity-80">Gap to target</div>
          <div className="text-3xl font-bold leading-tight">{fmtCr(g.gap)}</div>
          <div className="text-[10px] opacity-80 mt-0.5">{g.daysRemaining} days remaining</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider opacity-80">Need per day</div>
          <div className="text-3xl font-bold leading-tight">{fmtCr(g.neededPerDay)}</div>
          <div className="text-[10px] opacity-80 mt-0.5">to hit {fmtCr(g.target)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider opacity-80">You're at</div>
          <div className="text-3xl font-bold leading-tight">{fmtCr(g.realised)}</div>
          <div className="text-[10px] opacity-80 mt-0.5">{g.realisedPct.toFixed(1)}% of target</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider opacity-80">Current pace</div>
          <div className="text-3xl font-bold leading-tight">{fmtCr(g.currentPace)}</div>
          <div className="text-[10px] opacity-80 mt-0.5">per day · {fmtCr(g.currentPace * 7)}/wk</div>
        </div>
      </div>

      {/* projection footer — the killer line */}
      <div className="px-5 py-3 flex items-center justify-between flex-wrap gap-3"
        style={{ background: p.stripe, color: g.color === 'green' ? '#1f5736' : g.color === 'amber' ? '#7a4f0b' : '#7a1f1f' }}>
        <div className="flex items-center gap-2">
          {g.color === 'green'
            ? <TrendingUp size={16} />
            : <TrendingDown size={16} />}
          <span className="text-sm font-semibold"
                title="Linear extrapolation of realised-to-date over remaining days. If a large advance was paid upfront, projection will overstate — recurring revenue isn't actually flowing at that rate.">
            At current pace, you'll close FY at <span className="font-bold">{fmtCr(g.projectedFy)}</span>
            {g.color === 'green'
              ? <span> — <span className="font-bold">{fmtCr(g.aheadBy)} AHEAD</span> of target 🎯</span>
              : <span> — <span className="font-bold">{fmtCr(g.shortBy)} SHORT</span> of target</span>}
          </span>
        </div>
        <div className="flex items-center gap-3">
          {showCatchUpBtn && (
            <button
              onClick={() => setShowCatchUp(true)}
              data-testid={`catchup-trigger-${divisionId}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold transition-all hover:scale-105"
              style={{ background: g.color === 'red' ? '#7a1f1f' : '#7a4f0b', color: p.accent, border: `1px solid ${p.accent}` }}
            >
              <Sparkles size={12} /> How do I catch up?
            </button>
          )}
          <span className="text-[11px] font-medium" title={`Realised ₹${(g.realised/1e7).toFixed(2)}Cr ÷ ${g.dayOfFy} days × 365 = ₹${(g.projectedFy/1e7).toFixed(2)}Cr projection. Linear extrapolation — caveat for upfront advances.`}>
            Projection: {g.projectedPct.toFixed(0)}% of {fmtCr(g.target)} target
          </span>
        </div>
      </div>

      {compact ? null : (
        <div className="h-1.5 w-full" style={{ backgroundColor: 'rgba(255,255,255,0.18)' }}>
          <div className="h-full" style={{
            width: `${Math.min(g.realisedPct, 100)}%`,
            backgroundColor: p.accent,
          }} />
        </div>
      )}

      {showCatchUp && <CatchUpModal divisionId={divisionId} onClose={() => setShowCatchUp(false)} />}
    </div>
  );
}

// ---------- group (CEO) banner ------------------------------------------

export function GroupGapScreamer({ target, realised, divisions = [] }) {
  const [openDivId, setOpenDivId] = useState(null);
  if (!target || target <= 0) return null;
  const g = computeGap({ target, realised });
  const p = palette[g.color];
  const Icon = p.icon;

  // pre-compute per-division gap rows
  const rows = divisions
    .filter(d => Number(d.annual_target) > 0)
    .map(d => ({
      ...d,
      _g: computeGap({ target: d.annual_target, realised: d.ytd_revenue }),
    }))
    .sort((a, b) => a._g.realisedPct - b._g.realisedPct); // worst first

  return (
    <div data-testid="gap-screamer-group" className="rounded-xl shadow-lg overflow-hidden"
      style={{ background: p.bg }}>
      <div className="px-5 pt-4 pb-2 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Icon size={22} style={{ color: p.accent }} />
          <span className="text-[12px] tracking-[0.18em] font-bold" style={{ color: p.accent }}>
            GROUP {p.label} · LES CONCIERGES
          </span>
        </div>
        <span className="text-[11px]" style={{ color: p.accent, opacity: 0.9 }}>
          Day {g.dayOfFy} of {g.totalDays} · {g.fyLabel}
        </span>
      </div>

      <div className="px-5 pb-4 grid grid-cols-2 sm:grid-cols-4 gap-4 text-white">
        <div>
          <div className="text-[10px] uppercase tracking-wider opacity-80" title="Target − Realised. Pure cash-in shortfall (does not credit signed contracts).">Unrealised gap</div>
          <div className="text-3xl font-bold leading-tight">{fmtCr(g.gap)}</div>
          <div className="text-[10px] opacity-80 mt-0.5">of {fmtCr(g.target)} target</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider opacity-80">Need / day</div>
          <div className="text-3xl font-bold leading-tight">{fmtCr(g.neededPerDay)}</div>
          <div className="text-[10px] opacity-80 mt-0.5">across {g.daysRemaining} days</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider opacity-80">Realised</div>
          <div className="text-3xl font-bold leading-tight">{fmtCr(g.realised)}</div>
          <div className="text-[10px] opacity-80 mt-0.5">{g.realisedPct.toFixed(1)}% complete</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider opacity-80">Current pace</div>
          <div className="text-3xl font-bold leading-tight">{fmtCr(g.currentPace)}</div>
          <div className="text-[10px] opacity-80 mt-0.5">per day group-wide</div>
        </div>
      </div>

      {/* projection */}
      <div className="px-5 py-3 flex items-center justify-between flex-wrap gap-3"
        style={{ background: p.stripe, color: g.color === 'green' ? '#1f5736' : g.color === 'amber' ? '#7a4f0b' : '#7a1f1f' }}>
        <div className="flex items-center gap-2">
          <Target size={16} />
          <span className="text-sm font-semibold">
            Projected FY close: <span className="font-bold">{fmtCr(g.projectedFy)}</span>
            {g.color === 'green'
              ? <span> — <span className="font-bold">{fmtCr(g.aheadBy)} AHEAD</span> 🎯</span>
              : <span> — <span className="font-bold">{fmtCr(g.shortBy)} SHORT</span></span>}
          </span>
        </div>
        <span className="text-[11px] font-medium">
          {g.projectedPct.toFixed(0)}% of {fmtCr(g.target)}
        </span>
      </div>

      {/* per-division gap table */}
      {rows.length > 0 && (
        <div className="px-3 sm:px-5 pb-4 pt-3" style={{ background: 'rgba(255,255,255,0.06)' }}>
          <div className="text-[10px] uppercase tracking-wider mb-2" style={{ color: p.accent, opacity: 0.85 }}>
            Division gaps (worst first)
          </div>
          <div className="space-y-1.5">
            {rows.map(d => {
              const dotColor = d._g.color === 'green' ? '#3DD68C' : d._g.color === 'amber' ? '#FFC555' : '#FF6B6B';
              const canCatchUp = d._g.color !== 'green';
              return (
                <div key={d.id}
                  onClick={canCatchUp ? () => setOpenDivId(d.id) : undefined}
                  className={`grid grid-cols-12 gap-2 items-center text-[12px] text-white py-1 px-2 rounded ${canCatchUp ? 'cursor-pointer hover:bg-white/10' : ''}`}
                  data-testid={`group-gap-row-${d.id}`}>
                  <div className="col-span-3 sm:col-span-2 font-semibold uppercase tracking-wider truncate">
                    <span className="inline-block w-2 h-2 rounded-full mr-2" style={{ backgroundColor: dotColor }} />
                    {d.id}
                  </div>
                  <div className="col-span-3 sm:col-span-2 text-right font-medium opacity-90">
                    {fmtCr(d.annual_target)}
                  </div>
                  <div className="col-span-3 sm:col-span-2 text-right font-medium opacity-90">
                    {fmtCr(d.ytd_revenue)}
                  </div>
                  <div className="col-span-3 sm:col-span-3 text-right font-bold">
                    Gap {fmtCr(d._g.gap)}
                  </div>
                  <div className="hidden sm:block sm:col-span-3 text-right text-[11px]">
                    Pace → {fmtCr(d._g.projectedFy)}{' '}
                    <span style={{ color: dotColor }}>
                      ({d._g.projectedPct.toFixed(0)}%)
                    </span>
                    {canCatchUp && <Sparkles size={11} className="inline ml-2 opacity-80" />}
                  </div>
                </div>
              );
            })}
          </div>
          <p className="text-[10px] mt-2 italic opacity-75" style={{ color: p.accent }}>
            Click a red/amber row → see Mira's catch-up plan for that division.
          </p>
        </div>
      )}
      {openDivId && <CatchUpModal divisionId={openDivId} onClose={() => setOpenDivId(null)} />}
    </div>
  );
}

// ---------- catch-up plan modal ----------------------------------------

function CatchUpModal({ divisionId, onClose }) {
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState(null);

  React.useEffect(() => {
    let alive = true;
    api.get(`/catch-up/${divisionId}`)
      .then(r => { if (alive) setPlan(r.data); })
      .catch(e => { if (alive) setError(e?.response?.data?.detail || 'Failed to load plan'); });
    return () => { alive = false; };
  }, [divisionId]);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      style={{ background: 'rgba(11,17,32,0.78)', backdropFilter: 'blur(4px)' }}
      data-testid="catchup-modal" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[88vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 flex items-center justify-between border-b" style={{ borderColor: '#E5E1DC' }}>
          <div className="flex items-center gap-2">
            <Sparkles size={18} style={{ color: '#2A7B88' }} />
            <h3 className="text-base font-bold" style={{ color: '#1B2A4A' }}>
              {plan ? `Catch-Up Plan · ${plan.division_name}` : 'Catch-Up Plan'}
            </h3>
          </div>
          <button onClick={onClose} className="p-1 rounded-md hover:bg-[#F7F5F2]" data-testid="catchup-close">
            <X size={18} style={{ color: '#6B7280' }} />
          </button>
        </div>

        {error && <div className="p-6 text-sm" style={{ color: '#A32D2D' }}>{error}</div>}
        {!plan && !error && <div className="p-8 text-center text-sm" style={{ color: '#6B7280' }}>Computing your three paths…</div>}

        {plan && (
          <div className="px-6 py-5 space-y-5">
            {/* hero gap line */}
            <div className="rounded-xl p-4 text-white" style={{ background: 'linear-gradient(135deg,#1B2A4A 0%,#2A7B88 100%)' }}>
              <div className="text-[10px] uppercase tracking-[0.18em] opacity-80">Gap to close · {plan.fy_label}</div>
              <div className="text-3xl font-bold mt-1">{fmtCr(plan.gap)}</div>
              <div className="text-[11px] opacity-85 mt-1">
                {fmtCr(plan.realised)} of {fmtCr(plan.target)} · {plan.days_remaining} days · {plan.months_remaining} months remaining
              </div>
            </div>

            <p className="text-[13px]" style={{ color: '#6B7280' }}>
              Three concrete paths Mira computed from your live pipeline + contracts. Pick one — or combine them.
            </p>

            {/* Path A — New Deals */}
            <CatchUpPathCard
              icon={Trophy}
              tag="OPTION A"
              title={plan.paths.new_deals.title}
              actionable={plan.paths.new_deals.actionable}
              testId="catchup-path-new-deals"
            >
              <p className="text-[14px] font-semibold leading-snug" style={{ color: '#1B2A4A' }}>
                {plan.paths.new_deals.headline}
              </p>
              {plan.paths.new_deals.actionable && (
                <p className="text-[11px] mt-1" style={{ color: '#6B7280' }}>
                  Based on your historical avg won deal size (₹{_short(plan.paths.new_deals.avg_deal_size)}).
                </p>
              )}
            </CatchUpPathCard>

            {/* Path B — Accelerate */}
            <CatchUpPathCard
              icon={Rocket}
              tag="OPTION B"
              title={plan.paths.accelerate.title}
              actionable={plan.paths.accelerate.actionable}
              testId="catchup-path-accelerate"
            >
              <p className="text-[14px] font-semibold leading-snug" style={{ color: '#1B2A4A' }}>
                {plan.paths.accelerate.headline}
              </p>
              {plan.paths.accelerate.deals_to_close.length > 0 && (
                <div className="mt-2 space-y-1">
                  {plan.paths.accelerate.deals_to_close.slice(0, 6).map((d, i) => (
                    <div key={i} className="flex items-center justify-between text-[12px] py-1.5 px-2 rounded-md hover:bg-[#F7F5F2]">
                      <div className="flex-1 min-w-0">
                        <span className="font-medium truncate" style={{ color: '#1B2A4A' }}>{d.deal_name}</span>
                        {d.owner && <span className="text-[10px] ml-2" style={{ color: '#9CA3AF' }}>· {d.owner}</span>}
                      </div>
                      <span className="text-[11px] mr-3" style={{ color: '#6B7280' }}>{d.stage}</span>
                      <span className="font-semibold tabular-nums" style={{ color: '#2A7B88' }}>{fmtCr(d.deal_value)}</span>
                    </div>
                  ))}
                  {plan.paths.accelerate.deals_to_close.length > 6 && (
                    <p className="text-[11px] mt-1" style={{ color: '#9CA3AF' }}>+{plan.paths.accelerate.deals_to_close.length - 6} more deals listed in Pipeline tab.</p>
                  )}
                </div>
              )}
            </CatchUpPathCard>

            {/* Path C — MRR */}
            <CatchUpPathCard
              icon={Repeat}
              tag="OPTION C"
              title={plan.paths.mrr.title}
              actionable={plan.paths.mrr.actionable}
              testId="catchup-path-mrr"
            >
              <p className="text-[14px] font-semibold leading-snug" style={{ color: '#1B2A4A' }}>
                {plan.paths.mrr.headline}
              </p>
              <p className="text-[11px] mt-1" style={{ color: '#6B7280' }}>
                Current pace: {fmtCr(plan.paths.mrr.current_monthly_pace)}/month · target pace: {fmtCr(plan.paths.mrr.target_monthly)}/month.
              </p>
            </CatchUpPathCard>

            <p className="text-[10px] text-center pt-2" style={{ color: '#9CA3AF' }}>
              Numbers recompute every time revenue is added. Ask Mira "what's our catch-up plan?" anytime.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function CatchUpPathCard({ icon: Icon, tag, title, actionable, children, testId }) {
  return (
    <div data-testid={testId} className="rounded-xl border p-4"
      style={{ borderColor: actionable ? '#E5E1DC' : '#F3F4F6', background: actionable ? '#FFFFFF' : '#FAFAF8', opacity: actionable ? 1 : 0.7 }}>
      <div className="flex items-center gap-2 mb-1.5">
        <Icon size={14} style={{ color: '#2A7B88' }} />
        <span className="text-[10px] tracking-[0.15em] font-bold" style={{ color: '#2A7B88' }}>{tag}</span>
        <span className="text-[10px]" style={{ color: '#9CA3AF' }}>· {title}</span>
      </div>
      {children}
    </div>
  );
}

function _short(v) {
  const n = Number(v) || 0;
  if (n >= 1e7) return `${(n/1e7).toFixed(2)}Cr`;
  if (n >= 1e5) return `${(n/1e5).toFixed(2)}L`;
  if (n >= 1e3) return `${(n/1e3).toFixed(1)}K`;
  return Math.round(n).toLocaleString('en-IN');
}

export default DivisionGapScreamer;
